import React, { useCallback, useEffect, useState, useRef } from 'react'
import clsx from 'clsx'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { ExpandMoreOutlined } from '@material-ui/icons'
import {
  Button,
  Collapse,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core'

import { useUserProfile } from 'commons/useUserProfile'

import Loader from './Loader'
import useComments from './useComments'
import CommentBox from './CommentBox/CommentBox'
import CommentContainer from './CommentContainer'

const useStyles = makeStyles((theme) => ({
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  label: { textWrap: 'nowrap' },
}))

const CommentsCollapse = (props) => {
  const { id, isExplain, onFetchTimelineWithoutLoader } = props
  const classes = useStyles()
  const profile = useUserProfile()
  const { formatMessage } = useIntl()

  const {
    state: {
      count,
      comments,
      isLoadingList,
      isSubmitSuccess,
      isSubmiitingComment,
    },
    reFetchCommentList,
    onSubmittedComment,
    handlefetchNotificationComments,
  } = useComments(id, profile.id, onFetchTimelineWithoutLoader)

  const commentRef = useRef(null)
  const [expanded, setExpanded] = useState(isExplain)

  const formik = useFormik({
    initialValues: {
      text: '',
    },
  })

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const handleFetchComments = useCallback(() => {
    handlefetchNotificationComments()
  }, [handlefetchNotificationComments])

  useEffect(() => {
    setExpanded(isExplain)
  }, [isExplain])

  useEffect(() => {
    return () => {
      formik.resetForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleFetchComments, [])

  useEffect(() => {
    if (expanded)
      setTimeout(() => {
        commentRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }, 500)
    else return
  }, [expanded])

  return (
    <div ref={commentRef}>
      <div className="flex justify-between">
        <div className="w-full flex -items-center" onClick={handleExpandClick}>
          <Typography className="flex items-center font-bold text-16 text-primary">
            {formatMessage(
              { id: 'timeline.detail.message.count.comments' },
              { commentCount: count },
            )}
          </Typography>
        </div>
        <div className="flex">
          {expanded ? (
            <Button
              className={clsx(
                'p-0 text-secondary text-14 font-400 normal-case disabled:text-grey-200 hover:bg-transparent text-nowrap',
                classes.label,
              )}
              onClick={handlefetchNotificationComments}
              disabled={isLoadingList || isSubmiitingComment}
            >
              {formatMessage({ id: 'refresh.label' })}
            </Button>
          ) : null}

          <IconButton
            className={clsx(classes.expand, {
              [classes.expandOpen]: expanded,
            })}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <ExpandMoreOutlined />
          </IconButton>
        </div>
      </div>

      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit
        className="min-h-[350px]"
      >
        <CommentContainer
          isClose={false}
          submitText={formatMessage({
            id: 'timeline.detail.message.comment',
          })}
          isSuccess={isSubmitSuccess}
          placeholder={`${formatMessage({ id: 'timeline.detail.message.add.comment' })}....`}
          isLoading={isSubmiitingComment}
          onSubmit={async (value) => await onSubmittedComment(value)}
        />
        {isSubmitSuccess && isSubmiitingComment ? (
          <Loader className="py-10" />
        ) : null}
        {isLoadingList ? (
          <div className="divide-y divide-neutrals-200">
            <Loader className="py-10" />
            <Loader className="py-10" />
            <Loader className="py-10" />
          </div>
        ) : (
          <div className="divide-y divide-neutrals-200">
            {comments.map((comment) => (
              <div key={comment.id}>
                <CommentBox
                  id={id}
                  data={comment}
                  onFetchCommentList={() => reFetchCommentList}
                />
              </div>
            ))}
          </div>
        )}
      </Collapse>
    </div>
  )
}

CommentsCollapse.propTypes = {
  id: PropTypes.number,
  isExplain: PropTypes.bool,
  onFetchTimelineWithoutLoader: PropTypes.func,
}

export default CommentsCollapse
